// Portfolio — main app
import { Link, useNavigate } from '@tanstack/react-router';
import InteractiveBlob from './InteractiveBlob';
import ThemeToggle from './ThemeToggle';
import CommandPalette, { OPEN_PALETTE_EVENT } from './CommandPalette';
import type { PaletteAction } from './CommandPalette';
import { setTheme } from './theme';

const BUILD_DATE = new Date(__BUILD_DATE__);

function Portfolio() {
  const navigate = useNavigate();

  const actions: readonly PaletteAction[] = [
    {
      id: 'work',
      label: 'selected work',
      hint: 'jump to projects',
      run: () => document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' }),
    },
    {
      id: 'axolotl',
      label: 'play axolotl sound sequence',
      hint: 'the game',
      run: () => void navigate({ to: '/projects/axolotl' }),
    },
    {
      id: 'github',
      label: 'github',
      hint: '@bmiczek',
      run: () => window.open('https://github.com/bmiczek', '_blank', 'noopener'),
    },
    {
      id: 'linkedin',
      label: 'linkedin',
      hint: '/in/bart-miczek',
      run: () =>
        window.open('https://www.linkedin.com/in/bart-miczek-64271939/', '_blank', 'noopener'),
    },
    {
      id: 'theme-light',
      label: 'theme: light',
      hint: 'warm & bright',
      run: () => setTheme('light'),
    },
    { id: 'theme-dark', label: 'theme: dark', hint: 'lights out', run: () => setTheme('dark') },
    {
      id: 'theme-paper',
      label: 'theme: paper',
      hint: 'old newsprint',
      run: () => setTheme('paper'),
    },
  ];

  return (
    <div className="page">
      <Topbar />
      <Hero />
      <Projects />
      <Links />
      <Foot />
      <CommandPalette actions={actions} />
    </div>
  );
}

function Topbar() {
  const month = BUILD_DATE.toLocaleString('en-US', { month: 'short' }).toLowerCase();
  return (
    <div className="topbar">
      <div className="mark">
        <span className="dot"></span>
        <span>portfolio · {BUILD_DATE.getFullYear()}</span>
      </div>
      <div className="meta">
        <span>last updated · {month}</span>
        <button
          className="palette-open"
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent(OPEN_PALETTE_EVENT))}
          aria-label="Open command palette"
        >
          ⌘K
        </button>
        <ThemeToggle />
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="hero">
      <div>
        <div className="status">
          <span className="pulse"></span>
          <span>not looking, but always curious</span>
        </div>
        <p className="eyebrow">Senior Engineer · maker of small, useful things</p>
        <h1>
          Small software,
          <br />
          built with <em>unreasonable</em>
          <br />
          care.
        </h1>
        <p className="lead">
          By day I keep big systems honest. By night I ship weekend-sized ideas — tight feedback
          loops, opinionated tools, and small things that quietly compound.
        </p>
        <div className="hero-meta">
          <div>
            <span className="label">based</span>somewhere with wifi
          </div>
          <div>
            <span className="label">stack</span>typescript · react · tanstack · python · claude code
          </div>
        </div>
      </div>
      <InteractiveBlob />
    </section>
  );
}

interface Project {
  num: string;
  title: string;
  tag: string;
  blurb: string;
  stack: readonly string[];
  href?: string;
  to?: string;
}

const PROJECTS: readonly Project[] = [
  {
    num: '01',
    title: 'Afloat',
    tag: 'in progress',
    blurb:
      'A personal bill tracker that keeps your cash-flow head above water. Forecasts, due-date nudges, and the smallest set of inputs that still tells the truth.',
    stack: ['react', 'tanstack start', 'sqlite'],
    href: '#',
  },
  {
    num: '02',
    title: 'App Template',
    tag: 'open source',
    blurb:
      'A production-ready monorepo starter I build apps from. Auth, email, job queues, and type-safe data pre-wired so day one feels like day three.',
    stack: ['tanstack start', 'prisma', 'tailwind'],
    href: 'https://github.com/bmiczek/app-template',
  },
  {
    num: '03',
    title: 'Axolotl Sound Sequence',
    tag: 'for my daughter',
    blurb:
      'A sound-matching memory game vibe-coded one weekend. You tap an axolotl, it sings a note, you repeat the sequence. She beats me at it routinely.',
    stack: ['html', 'web audio', 'love'],
    to: '/projects/axolotl',
  },
];

function Projects() {
  return (
    <section className="block" id="work">
      <div className="section-head">
        <h2>Selected work</h2>
        <span className="index">03 things, currently</span>
      </div>
      <div className="projects">
        {PROJECTS.map((p) => {
          const inner = (
            <>
              <span className="num">{p.num}</span>
              <div className="body">
                <h3>
                  {p.title}
                  <span className="tag">{p.tag}</span>
                </h3>
                <p>{p.blurb}</p>
                <div className="stack">
                  {p.stack.map((s, i) => (
                    <span key={i}>{s}</span>
                  ))}
                </div>
              </div>
              <span className="arrow" aria-hidden>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M3 13L13 3M13 3H5M13 3V11"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </>
          );
          return p.to ? (
            <Link className="project" to={p.to} key={p.num}>
              {inner}
            </Link>
          ) : (
            <a
              className="project"
              href={p.href ?? '#'}
              key={p.num}
              target="_blank"
              rel="noreferrer"
            >
              {inner}
            </a>
          );
        })}
      </div>
    </section>
  );
}

interface SocialLink {
  glyph: string;
  label: string;
  handle: string;
  href: string;
}

const LINKS: readonly SocialLink[] = [
  {
    glyph: 'in / 01',
    label: 'LinkedIn',
    handle: '/in/bart-miczek-64271939',
    href: 'https://www.linkedin.com/in/bart-miczek-64271939/',
  },
  {
    glyph: 'gh / 02',
    label: 'GitHub',
    handle: '@bmiczek',
    href: 'https://github.com/bmiczek',
  },
];

function Links() {
  return (
    <section className="block" id="links">
      <div className="section-head">
        <h2>Find me</h2>
        <span className="index">↳ say hi</span>
      </div>
      <div className="links">
        {LINKS.map((l, i) => (
          <a className="link-card" href={l.href} key={i} target="_blank" rel="noreferrer">
            <div>
              <div className="glyph">{l.glyph}</div>
            </div>
            <div>
              <div className="label">{l.label}</div>
              <div className="handle">{l.handle}</div>
            </div>
            <span className="corner">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M3 13L13 3M13 3H5M13 3V11"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </a>
        ))}
      </div>
    </section>
  );
}

function Foot() {
  return (
    <footer className="foot">
      <div>© {new Date().getFullYear()} · handcrafted with Claude</div>
      <div className="foot-hint">
        press <kbd>⌘K</kbd> — there’s a palette
      </div>
      <div className="now">
        currently · <em>shipping afloat v0.3</em>
      </div>
    </footer>
  );
}

export default Portfolio;
